import React from "react";
import { useOutletContext } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Navigation, ExternalLink, Clock } from "lucide-react";
import { useQuote } from "@/components/QuoteProvider";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";

export default function ContactPage() {
  const { settings } = useOutletContext() || { settings: {} };
  const s = settings || {};
  const { openQuote } = useQuote();
  useSeo({
    title: "Contact SSK Enterprises — Request a Quotation for NDT Materials & Bearings in Hyderabad",
    description:
      "Contact SSK Enterprises in Kukatpally, Hyderabad for MR Chemie NDT materials, KLYDE zinc coatings and industrial bearings. Call +91 8309352443, WhatsApp us, or send an enquiry to sales@ssk21.net.",
    path: "/contact",
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact Us", path: "/contact" }])],
  });

  const waNumber = (s.whatsapp || "+91XXXXXXXXXX").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hello SSK Enterprises, I would like to request a quotation.")}`;
  const emailAddr = s.email || "sales@ssk21.net";
  const emailHref = `mailto:${emailAddr}?subject=${encodeURIComponent("Product Enquiry – SSK Enterprises")}`;
  const fullAddress = s.address || "Hyderabad, Telangana, India";
  // Prefer explicit embed URL, else build from address
  const embedSrc = s.map_embed || `https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`;
  const gbpUrl = s.gbp_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;

  return (
    <>
      <section className="bg-[#0F1E3B] text-white border-b border-slate-800">
        <div className="container-x py-16">
          <div className="overline text-slate-400 mb-3">Contact &amp; Request Quotation</div>
          <h1 className="font-display text-4xl md:text-5xl font-black">Get in touch with SSK Enterprises</h1>
          <p className="text-slate-300 mt-4 max-w-2xl">Share your product requirement — our team in Kukatpally, Hyderabad will respond with pricing, packaging and delivery details.</p>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-4">
            {/* 1. WhatsApp (primary) */}
            <ContactCard icon={MessageCircle} label="WhatsApp" value={s.whatsapp || "+91 83093 52443"} href={waUrl} external testId="contact-whatsapp" />
            {/* 2. Calls (primary) */}
            <ContactCard icon={Phone} label="Calls" value={s.phone || "+91 81067 19123"} href={`tel:${(s.phone || "").replace(/\s/g,"")}`} testId="contact-phone" />
            {/* 3. Additional Contact — display only, no button */}
            {s.phone_sales && (
              <ContactCard icon={Phone} label="Additional Contact" value={s.phone_sales} testId="contact-phone-additional" />
            )}
            {/* 4. Email */}
            <ContactCard icon={Mail} label="Email Us" value={emailAddr} href={emailHref} testId="contact-email" />
            {/* 5. Business Hours */}
            <div className="tech-card p-5 flex gap-4 items-start" data-testid="business-hours">
              <div className="w-10 h-10 bg-[#B5121B] text-white flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5" /></div>
              <div className="flex-grow">
                <div className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">Business Hours</div>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between gap-6 text-[#1E293B] font-medium text-sm">
                    <span>Monday – Saturday</span>
                    <span>9:00 AM – 7:00 PM</span>
                  </div>
                  <div className="flex justify-between gap-6 text-slate-500 text-sm">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>
            <address className="tech-card p-5 not-italic flex gap-4 items-start" data-testid="contact-address">
              <div className="w-10 h-10 bg-[#0F2747] text-white flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5" /></div>
              <div>
                <div className="overline">Registered Address</div>
                <div className="text-[#1E293B] mt-1 leading-relaxed">{fullAddress}</div>
              </div>
            </address>
            {s.gst && s.gst !== "GSTIN-XXXXXXXXX" && (
              <div className="tech-card p-5">
                <div className="overline">GSTIN</div>
                <div className="font-mono-tech text-[#1E293B] mt-1">{s.gst}</div>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 space-y-6">
            <div className="tech-card p-8">
              <h2 className="font-display text-2xl font-bold text-[#1E293B]">Request a Quotation</h2>
              <p className="text-slate-600 mt-2 text-sm">Open the full quotation form to share product, quantity, packaging, application and attachments.</p>
              <button onClick={() => openQuote()} className="btn-primary mt-6 w-full" data-testid="contact-open-quote">Open Quotation Form</button>
              <div className="mt-8 pt-8 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <a href={`tel:${(s.phone || "").replace(/\s/g,"")}`} className="btn-outline text-sm"><Phone className="w-4 h-4" /> Call</a>
                <a href={waUrl} target="_blank" rel="noreferrer" className="btn-outline text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                <a href={emailHref} className="btn-outline text-sm" data-testid="contact-cta-email"><Mail className="w-4 h-4" /> Email</a>
              </div>
              <div className="mt-3 text-center text-xs text-slate-500">
                Or copy our email:{" "}
                <a href={emailHref} className="font-mono-tech font-semibold text-[#0F2747] hover:text-[#B5121B] select-all" data-testid="contact-cta-email-copy">{emailAddr}</a>
              </div>
            </div>

            {/* Google Map Embed */}
            <div className="tech-card overflow-hidden">
              <div className="aspect-[16/10] bg-slate-100">
                <iframe
                  title="SSK Enterprises location on Google Maps"
                  src={embedSrc}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="google-map-embed"
                ></iframe>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a href={gbpUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="view-on-gmaps">
                  <ExternalLink className="w-4 h-4" /> View on Google Maps
                </a>
                <a href={directionsUrl} target="_blank" rel="noreferrer" className="btn-outline" data-testid="contact-directions">
                  <Navigation className="w-4 h-4" /> Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, label, value, href, external, testId }) {
  const inner = (
    <div className="tech-card p-5 flex gap-4 items-start" data-testid={testId}>
      <div className="w-10 h-10 bg-[#0F2747] text-white flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="overline">{label}</div>
        <div className="text-[#1E293B] font-medium mt-1 break-all">{value}</div>
      </div>
    </div>
  );
  if (!href) return inner;
  return <a href={href} target={external ? "_blank" : undefined} rel="noreferrer" className="block">{inner}</a>;
}
